import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Empty, Input, Space,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import { BarcodeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import BrandLogo from '../components/BrandLogo';
import ProductCardDrawer from '../components/product-card/ProductCardDrawer';
import PackageMarks from '../components/product-card/PackageMarks';
import { packageColumns } from '../components/product-card/packageColumns';
import { num } from '../components/product-card/format';
import {
  WarehouseLiquidRow, WarehousePackageRow, WarehouseStockRow,
  getWarehouseLiquids, syncWarehouseLiquids,
} from '../api/warehouse';

const { Title, Text, Link } = Typography;

const errMsg = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg).filter(Boolean).join('; ') || fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const WarehouseLiquidsPage: React.FC = () => {
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [warehouseName, setWarehouseName] = useState('');
  const [rows, setRows] = useState<WarehouseLiquidRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'liquids' | 'parts'>('liquids');

  const [cardTarget, setCardTarget] = useState<{ catalogId?: number; itemId?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWarehouseLiquids();
      setSyncedAt(data.synced_at);
      setWarehouseName(data.warehouse_name);
      setRows(data.rows);
    } catch (e: any) {
      message.error(errMsg(e, 'Не удалось загрузить снимок склада'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncWarehouseLiquids();
      message.success(`Снимок обновлён: ${num(res.synced)} строк`);
      await load();
    } catch (e: any) {
      message.error(errMsg(e, 'Не удалось обновить снимок из 1С'));
    } finally {
      setSyncing(false);
    }
  };

  const openCard = (r: WarehouseLiquidRow) => {
    setCardTarget(r.catalog_id != null ? { catalogId: r.catalog_id } : { itemId: r.item_id });
  };

  const liquidsCount = useMemo(() => rows.filter(r => r.is_liquid).length, [rows]);
  const partsCount = useMemo(() => rows.filter(r => !r.is_liquid).length, [rows]);

  const filtered = useMemo(() => {
    let data = rows.filter(r => tab === 'liquids' ? r.is_liquid : !r.is_liquid);
    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter(r =>
        r.display_name.toLowerCase().includes(q)
        || (r.brand || '').toLowerCase().includes(q)
        || r.item_name.toLowerCase().includes(q)
        || (r.article || '').toLowerCase().includes(q)
        || r.packages.some(p =>
          (p.article || '').toLowerCase().includes(q)
          || p.item_name.toLowerCase().includes(q)
          || p.package_label.toLowerCase().includes(q)
        )
      );
    }
    return data;
  }, [rows, tab, search]);

  const renderAvailable = (stocks: WarehouseStockRow[]) => (
    <>
      {stocks.map((s, i) => (
        <span
          key={s.unit}
          style={s.available <= 0 ? { fontWeight: 600, color: '#cf1322' } : undefined}
        >
          {i > 0 && ' + '}{num(s.available)} {s.unit}
        </span>
      ))}
    </>
  );

  const renderReserve = (stocks: WarehouseStockRow[]) => {
    const parts = stocks.filter(s => s.to_ship > 0);
    if (parts.length === 0) return null;
    return (
      <span style={{ fontWeight: 600, color: '#cf1322' }}>
        резерв {parts.map(s => `${num(s.to_ship)} ${s.unit}`).join(' + ')}
      </span>
    );
  };

  const columns = [
    {
      title: 'ЧЗ',
      key: 'marked',
      width: 50,
      align: 'center' as const,
      render: (_: unknown, r: WarehouseLiquidRow) =>
        r.is_marked
          ? <BarcodeOutlined title="Маркируемый товар (Честный знак)" style={{ color: '#1677ff' }} />
          : null,
    },
    {
      title: 'Наименование',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (v: string, r: WarehouseLiquidRow) => (
        <Space size={6} wrap>
          {r.brand && <BrandLogo brand={r.brand} />}
          <Link
            style={{ fontWeight: 500 }}
            onClick={e => { e.stopPropagation(); openCard(r); }}
          >
            {(r.brand && r.model_name) ? r.model_name : v}
          </Link>
          {!r.linked && <Tag color="orange">не в каталоге</Tag>}
          {r.legacy_ml && <Tag color="purple">разлив-легаси</Tag>}
        </Space>
      ),
    },
    {
      title: 'Резерв',
      key: 'to_ship',
      width: 160,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseLiquidRow) => renderReserve(r.stocks),
    },
    {
      title: 'Доступно',
      key: 'available',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseLiquidRow) => renderAvailable(r.stocks),
    },
  ];

  // packageColumns — общий модуль components/product-card/packageColumns.tsx
  // (withSpacer=true — широкая развёртка списка).

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }} align="center" wrap>
        <Space align="baseline" wrap>
          <Title level={4} style={{ margin: 0 }}>Склад</Title>
          <Text type="secondary">{warehouseName}</Text>
          {syncedAt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              данные на {new Date(syncedAt).toLocaleString('ru-RU')}
            </Text>
          )}
        </Space>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={syncing}
          onClick={handleSync}
        >
          Обновить из 1С
        </Button>
      </Space>

      {!loading && !syncedAt && rows.length === 0 ? (
        <Card>
          <Empty description="Снимок склада пуст — нажмите «Обновить из 1С», чтобы загрузить остатки" />
        </Card>
      ) : (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Input
              placeholder="Поиск по наименованию, артикулу или фасовке"
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
          </Card>

          <Tabs
            activeKey={tab}
            onChange={k => setTab(k as 'liquids' | 'parts')}
            items={[
              { key: 'liquids', label: `Жидкости (${liquidsCount})` },
              { key: 'parts', label: `Запчасти (${partsCount})` },
            ]}
          />

          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="key"
            loading={loading}
            pagination={{ pageSize: 50, showSizeChanger: false, showTotal: t => `Всего: ${t}` }}
            size="small"
            scroll={{ x: 'max-content' }}
            className="warehouse-liquids-table"
            locale={{ emptyText: 'Нет данных' }}
            expandable={{
              expandRowByClick: true,
              rowExpandable: (r: WarehouseLiquidRow) => r.packages.length > 0,
              expandedRowRender: (r: WarehouseLiquidRow) => (
                <div style={{ margin: '4px 0' }}>
                  <Table
                    dataSource={r.packages}
                    columns={packageColumns(true, true)}
                    rowKey="item_id"
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    expandable={{
                      expandRowByClick: true,
                      expandedRowRender: (p: WarehousePackageRow) => <PackageMarks itemId={p.item_id} />,
                    }}
                  />
                  {r.packages_hidden > 0 && (
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                      Скрыто старых позиций 1С: {r.packages_hidden}
                    </Text>
                  )}
                </div>
              ),
            }}
            onRow={(r: WarehouseLiquidRow) => ({
              onClick: () => {
                if (r.packages.length === 0) openCard(r);
              },
            })}
          />
        </>
      )}

      <ProductCardDrawer
        open={cardTarget != null}
        onClose={() => setCardTarget(null)}
        context="warehouse"
        catalogId={cardTarget?.catalogId}
        itemId={cardTarget?.itemId}
      />
    </div>
  );
};

export default WarehouseLiquidsPage;
